import { NurseLayout } from "@/components/layout/nurse-layout";
import { Link } from "wouter";
import { ArrowLeft, Stethoscope, CheckCircle, BookOpen, AlertCircle } from "lucide-react";

const ACP_PILLARS = [
  { label: "Clinical Practice", desc: "Expert clinical practice using advanced assessment, diagnosis, and management skills across complex and undifferentiated presentations." },
  { label: "Leadership & Management", desc: "Leading teams, services, and systems. Clinical governance, quality improvement, and professional leadership." },
  { label: "Education", desc: "Formal and informal education of patients, students, and colleagues. Curriculum design and delivery at postgraduate level." },
  { label: "Research", desc: "Using, conducting, and disseminating research. Contributing to the evidence base for rheumatology nursing practice." },
];

const PRESCRIBING_STEPS = [
  { step: "1", title: "Eligibility", desc: "Hold an NMC PIN (any part of the register), be in a suitable practice environment, and have identified a designated medical practitioner (DMP) mentor." },
  { step: "2", title: "Choose a programme", desc: "Select a CQC-approved/NMC-approved Independent Prescribing programme (V300) at an HEI — typically 6–12 months part-time. Check BSc/MSc level suitability." },
  { step: "3", title: "Employer agreement", desc: "Get employer sign-off: they must confirm a DMP/prescribing mentor and a suitable practice environment where you will prescribe after qualification." },
  { step: "4", title: "Complete programme", desc: "Complete the V300 curriculum: pharmacology, consultation skills, legal/ethical framework, and 90 supervised prescribing hours with DMP." },
  { step: "5", title: "NMC annotation", desc: "Apply to have 'Independent and Supplementary Prescriber' annotated on your NMC registration. Fee applies." },
  { step: "6", title: "Prescribe safely", desc: "Prescribe only within your competence, your employer's formulary, and your scope of practice. Regular supervision and CPD are mandatory." },
];

const RHEUM_PRESCRIBING_SCOPE = [
  "DMARDs: methotrexate, leflunomide, hydroxychloroquine, sulfasalazine — initiation and monitoring adjustments",
  "Biologics: adalimumab, etanercept, rituximab — continuation prescribing and dose adjustments within agreed protocols",
  "Corticosteroids: oral prednisolone, IM/IV methylprednisolone for flare management",
  "Symptom management: NSAIDs, analgesics, proton pump inhibitors for DMARD-related GI protection",
  "Monitoring-triggered adjustments: dose reductions or holds based on blood test results within agreed parameters",
];

export default function AnpAcp() {
  return (
    <NurseLayout title="Advanced Practice & Prescribing">
      <Link href="/nurse/career">
        <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Career Hub
        </button>
      </Link>

      <div className="space-y-8">

        {/* Header */}
        <div className="bg-gradient-to-br from-blue-900 to-blue-950 rounded-2xl p-8 text-white">
          <div className="flex items-center gap-4 mb-3">
            <div className="bg-blue-800/60 p-3 rounded-xl">
              <Stethoscope className="h-7 w-7 text-blue-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Advanced Practice & Nurse Prescribing</h2>
              <p className="text-blue-300 text-sm">ANP · ACP · V300 Independent Prescribing · NHS England Framework</p>
            </div>
          </div>
          <p className="text-blue-200 text-sm leading-relaxed max-w-3xl">
            Advanced Clinical Practice (ACP) and Advanced Nurse Practitioner (ANP) roles represent the highest clinical tier for nurses. In rheumatology, ACPs run independent clinics, prescribe biologics and DMARDs, and work at consultant level within the MDT. Independent Prescribing (V300) is typically the gateway qualification.
          </p>
        </div>

        {/* ACP Four Pillars */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">NHS England ACP Credential — Four Pillars</h3>
          <p className="text-sm text-slate-500 mb-4">The NHS England ACP credential (launched 2023) requires evidence across all four pillars. Assessment is typically completed via an employer or HEI-managed portfolio.</p>
          <div className="grid gap-4 md:grid-cols-2">
            {ACP_PILLARS.map((pillar, i) => (
              <div key={i} className="bg-white rounded-xl border border-blue-200 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="h-7 w-7 rounded-full bg-blue-100 text-blue-800 text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <h4 className="font-bold text-slate-900">{pillar.label}</h4>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed pl-10">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* V300 Steps */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">Independent Prescribing (V300) — Step-by-Step</h3>
          <div className="space-y-3">
            {PRESCRIBING_STEPS.map((s) => (
              <div key={s.step} className="flex items-start gap-4 bg-white rounded-xl border border-slate-200 p-4">
                <span className="h-8 w-8 rounded-full bg-blue-700 text-white text-sm font-bold flex items-center justify-center shrink-0 mt-0.5">{s.step}</span>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{s.title}</p>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rheum scope */}
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6">
          <h3 className="font-bold text-teal-900 mb-3 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-teal-700" />
            Typical Prescribing Scope in Rheumatology Nursing
          </h3>
          <ul className="space-y-2">
            {RHEUM_PRESCRIBING_SCOPE.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-teal-800">
                <span className="h-1.5 w-1.5 rounded-full bg-teal-500 mt-2 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Always prescribe within your competence and formulary</p>
            <p className="text-sm text-amber-800 mt-1 leading-relaxed">
              Independent prescribing authority does not override your professional duty to prescribe only within your competence and your employer's agreed formulary or Patient Group Directions (PGDs). Discuss all advanced prescribing roles with your employer and ensure a clinical supervision arrangement is in place.
            </p>
          </div>
        </div>

        {/* Links */}
        <div className="flex flex-wrap gap-3">
          <a href="https://www.hee.nhs.uk/our-work/advanced-clinical-practice" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-blue-800 transition-colors">
            <Stethoscope className="h-4 w-4" /> NHS England ACP Framework
          </a>
          <a href="https://www.nmc.org.uk/education/becoming-a-nurse-midwife-nursing-associate/registered-nurse-prescribers/" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium hover:border-blue-300 hover:text-blue-700 transition-colors">
            <BookOpen className="h-4 w-4" /> NMC Prescribing Standards
          </a>
        </div>
      </div>
    </NurseLayout>
  );
}
